import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Mail,
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MapPin,
  Shield,
  Server,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailAnalyzerPage = () => {
  const { getAuthHeader } = useAuth();
  const [rawHeaders, setRawHeaders] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    if (!rawHeaders.trim() || rawHeaders.length < 50) {
      toast.error("Please paste valid email headers");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/analyze-headers`, 
        { raw_headers: rawHeaders },
        { headers: getAuthHeader() }
      );
      setAnalysis(response.data);
      toast.success("Headers analyzed successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const getAuthBadge = (result) => {
    if (result === "pass") return <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">PASS</Badge>;
    if (result === "fail") return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">FAIL</Badge>;
    if (result === "neutral") return <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">NEUTRAL</Badge>;
    return <Badge variant="outline">N/A</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="email-analyzer-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
          <Mail className="w-7 h-7 text-primary" />
          Email Header Analyzer
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Analyze email headers to find sender IP and detect spoofing
        </p>
      </div>

      {/* Instructions */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-2">How to get email headers:</p>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Gmail:</strong> Open email → Three dots menu → "Show original"</li>
            <li>• <strong>Outlook:</strong> Open email → File → Properties → "Internet headers"</li>
            <li>• <strong>Yahoo:</strong> Open email → Three dots → "View raw message"</li>
          </ul>
        </CardContent>
      </Card>

      {/* Input */}
      <Card className="bg-card/50 border-border">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Paste Raw Email Headers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="Paste the full email headers here..."
            value={rawHeaders}
            onChange={(e) => setRawHeaders(e.target.value)}
            className="min-h-[200px] font-mono text-xs bg-background"
            data-testid="headers-input"
          />
          <Button 
            onClick={handleAnalyze} 
            disabled={loading}
            className="gap-2"
            data-testid="analyze-btn"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            Analyze Headers
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {analysis && (
        <div className="space-y-6">
          {/* Spoofing Alert */}
          {analysis.is_likely_spoofed && (
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <div>
                  <p className="font-medium text-red-500">Likely Spoofed Email</p>
                  <p className="text-sm text-muted-foreground">
                    Multiple indicators suggest this email may be spoofed or forged.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary */}
          <Card className="bg-card/50 border-border" data-testid="analysis-results">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Email Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">From</p>
                  <p className="font-mono text-sm mt-1 break-all">{analysis.from || "Not found"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">To</p>
                  <p className="font-mono text-sm mt-1 break-all">{analysis.to || "Not found"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Subject</p>
                  <p className="text-sm mt-1">{analysis.subject || "Not found"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Date</p>
                  <p className="text-sm mt-1">{analysis.date || "Not found"}</p>
                </div>
              </div>
              
              <Separator />
              
              {/* Authentication Results */}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Authentication Results</p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">SPF:</span>
                    {getAuthBadge(analysis.spf_result)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">DKIM:</span>
                    {getAuthBadge(analysis.dkim_result)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">DMARC:</span>
                    {getAuthBadge(analysis.dmarc_result)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sender IPs */}
          {analysis.enriched_ips && analysis.enriched_ips.length > 0 && (
            <Card className="bg-card/50 border-border" data-testid="sender-ips">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Server className="w-4 h-4 text-primary" />
                  Sender IP Addresses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analysis.enriched_ips.map((ip, index) => (
                    <div 
                      key={index}
                      className="p-4 bg-background rounded border border-border"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <p className="font-mono text-primary text-lg">{ip.ip}</p>
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {ip.city && ip.country ? `${ip.city}, ${ip.country}` : ip.country || "Unknown"}
                          </div>
                          {ip.isp && (
                            <p className="text-xs text-muted-foreground mt-1">ISP: {ip.isp}</p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {ip.is_vpn && (
                            <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">VPN</Badge>
                          )}
                          {ip.is_proxy && (
                            <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">Proxy</Badge>
                          )}
                          {ip.is_datacenter && (
                            <Badge variant="outline">Datacenter</Badge>
                          )}
                          {ip.risk_score > 50 && (
                            <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                              Risk: {ip.risk_score}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mail Route */}
          {analysis.route && analysis.route.length > 0 && (
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-sm font-medium">Email Route (Hop by Hop)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analysis.route.map((hop, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-xs font-mono text-primary">{index + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          {hop.from_server && (
                            <>
                              <span className="font-mono text-muted-foreground truncate max-w-[150px]">{hop.from_server}</span>
                              <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            </>
                          )}
                          {hop.to_server && (
                            <span className="font-mono text-primary truncate max-w-[150px]">{hop.to_server}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warnings */}
          {analysis.warnings && analysis.warnings.length > 0 && (
            <Card className="bg-amber-500/5 border-amber-500/20">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-500">
                  <AlertTriangle className="w-4 h-4" />
                  Warnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analysis.warnings.map((warning, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <XCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      {warning}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailAnalyzerPage;
